import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import MuiLink from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import BugReportIcon from "@mui/icons-material/BugReport";
import { putAPICall, getAPICall, deleteAPICall } from "lib/callApi";
import {
  UPDATE_INTEGRATION_SETTINGS,
  GET_INTEGRATION_SETTINGS,
  DELETE_INTEGRATION,
} from "lib/constants";
import Swal from "sweetalert2";
import { enqueueSnackbar } from "notistack";
import IconButton from "@mui/material/IconButton";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import IntegrationLogo from "components/IntegrationLogo";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import TooltipLight from "utils/TooltipLight";
import ExpandedIntegrationForm from "./ExpandedIntegrationForm";

function IntegrationBackButton({ navigate }) {
  return (
    <IconButton
      size="large"
      color="inherit"
      variant="contained"
      onClick={() => navigate("/settings?selectedTab=integrations", { replace: true })}
      sx={{
        position: "absolute",
      }}
    >
      <Icon fontSize="medium">arrow_back</Icon>
    </IconButton>
  );
}

function ExpandedIntegration({ integrationName }) {
  const navigate = useNavigate();
  const [integration, setIntegration] = useState({});
  const [loading, setLoading] = useState(true);
  const [showErrorIcon, setShowErrorIcon] = useState(false);
  const [isRemovingIntegration, setIsRemovingIntegration] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const integrationId = integration?.integrationId;

  const getDetailedSettingsCb = useCallback(
    async (params = {}) => {
      try {
        const result = await getAPICall(
          `${GET_INTEGRATION_SETTINGS}/${integrationName}`,
          true,
          params
        );

        if (result?.data?.integration) {
          setIntegration(result.data.integration);
          setLoading(false);
        } else if (result?.response?.status === 401) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: result?.response?.data?.message || "Integration not found",
            }),
            {
              variant: "custom",
            }
          );
          navigate("/settings?selectedTab=integrations", { replace: true });
        } else {
          setShowErrorIcon(true);
          setLoading(false);
        }
      } catch (err) {
        console.log("Uncaught error exception encountered ");
        console.log(err);
        setShowErrorIcon(true);
        setLoading(false);
      }
    },
    [integrationName]
  );

  const showDeletionAndCleanupModal = useCallback(() => {
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to deactivate and cleanup this integration? This will remove your ${
        integration?.hasIntegrationPermissions && integration?.integrationAppliedLevel === "user"
          ? "company/user"
          : integration?.integrationAppliedLevel || "company"
      }'s settings from the Alysio system${
        integration?.integrationAppliedLevel === "company"
          ? " and remove any external webhooks that notify our system."
          : "."
      }`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Remove",
      reverseButtons: true,
      ...(integration?.hasIntegrationPermissions && integration?.integrationAppliedLevel === "user"
        ? {
            input: "radio",
            inputOptions: {
              company: "Company",
              user: "User",
            },
            inputValidator: (result) => (result ? null : "You need to select something!"),
          }
        : {}),
    }).then(async ({ isConfirmed, value }) => {
      if (isConfirmed) {
        try {
          setIsRemovingIntegration(true);
          const resultResponse = await deleteAPICall(
            `${DELETE_INTEGRATION}/${integrationId}${
              // eslint-disable-next-line no-nested-ternary
              integration?.hasIntegrationPermissions &&
              integration?.integrationAppliedLevel === "user"
                ? `?deleteFor=${value}`
                : integration?.integrationAppliedLevel === "user"
                ? `?deleteFor=user`
                : ""
            }`,
            true,
            null
          );

          if (resultResponse?.data?.success) {
            await getDetailedSettingsCb();
            enqueueSnackbar(
              JSON.stringify({
                icon: "success",
                title: "Integration Cleanup Success",
                text: "This integration's settings and external connections were removed.",
              }),
              {
                variant: "custom",
              }
            );
          } else {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: resultResponse?.data?.message || "Something Went Wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        } catch (err) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: err?.response?.data?.message || err?.message || "Something went wrong",
            }),
            {
              variant: "custom",
            }
          );
        } finally {
          setIsRemovingIntegration(false);
        }
      }
    });
  }, [integrationId, getDetailedSettingsCb]);

  useEffect(() => {
    if (integrationName) {
      getDetailedSettingsCb();
    }
  }, [integrationName]);

  if (loading || showErrorIcon || isRemovingIntegration) {
    return (
      <Box
        sx={{
          background: "white",
          padding: "2rem",
          width: "45rem",
          minHeight: "20rem",
          margin: "auto",
          marginTop: "3rem",
          position: "relative",
        }}
      >
        <IntegrationBackButton navigate={navigate} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: "4rem",
            justifyContent: "center",
          }}
        >
          <Typography variant="h2">{integrationName}</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          {showErrorIcon && (
            <>
              <BugReportIcon fontSize="large" />
              <Typography variant="p">
                Uh oh, we hit a snag loading this integration. Development has been notified.
              </Typography>
            </>
          )}
          {loading && <CircularProgress />}
        </Box>
      </Box>
    );
  }

  const {
    oAuthStartUrl,
    documentationUrl,
    showRemoveAndCleanupIcon,
    showRecalculateCacheIcon,
    reauthenticate,
    reauthenticateMessage,
    clientCredentialUrl,
  } = integration;

  const closeSnackbar = () => setShowSnackbar(false);

  // Allowing passing of cloned settings lets us handle for nested integration settings requesting immediate save so we don't get old state before cloned settings has finalized
  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const result = await putAPICall(
        `${UPDATE_INTEGRATION_SETTINGS}/${integration.integrationId}`,
        true,
        data
      );

      // Removes button row to prevent unintended user input during save process
      setHasChanges(false);
      // We need to fetch db state of integration settings and didn't hit unexpected error
      // loading state prevents any interaction with form during load
      setLoading(true);

      if (result.status === 200) {
        setShowSnackbar(true);
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              result?.response?.data?.errorMessage ||
              "Something went wrong! Development has been notified.",
          }),
          {
            variant: "custom",
          }
        );
      }

      // Fetch here so that we don't get state divergence from the server
      // Persisting change to db happens at the beginning of process so unless change is unexpected we should fetch state from server
      const refreshResult = await getAPICall(
        `${GET_INTEGRATION_SETTINGS}/${integrationName}`,
        true
      );

      if (refreshResult?.data?.integration) {
        setIntegration(refreshResult.data.integration);
      } else {
        // Best to throw to hard error state and let them refresh
        // If we didn't pull the integration from the server we'll get into weird state mismatch, force reload
        setShowErrorIcon(true);
      }
    } catch (err) {
      console.log("Unexpected error caught while saving integration state");
      console.error(err);
      setShowErrorIcon(true);
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const reCalculateCachedData = async () => {
    setLoading(true);
    await getDetailedSettingsCb({ reCalculateCachedData: true });
  };

  const handleAuthenticate = async () => {
    try {
      setSaving(true);
      const result = await putAPICall(clientCredentialUrl, true);
      if (result.status === 200) {
        setShowSnackbar(true);
        setHasChanges(false);
        const refreshResult = await getAPICall(
          `${GET_INTEGRATION_SETTINGS}/${integrationName}`,
          true
        );
        if (refreshResult?.data?.integration) {
          setIntegration(refreshResult.data.integration);
          setLoading(false);
        }
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text:
              result?.response?.data?.errorMessage ||
              "Something went wrong! Development has been notified.",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setSaving(false);
    }
  };
  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <Box
      sx={{
        background: "white",
        padding: "2rem",
        width: "90%",
        margin: "auto",
        marginTop: "3rem",
        position: "relative",
      }}
    >
      {(saving || isRemovingIntegration) && (
        <Box
          sx={{
            position: "absolute",
            height: "100%",
            width: "100%",
            backgroundColor: "#80808073",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100,
            top: 0,
            left: 0,
          }}
        >
          <CircularProgress
            sx={{
              width: "80px",
              height: "80px",
            }}
          />
        </Box>
      )}
      <IntegrationBackButton navigate={navigate} />

      <Box
        sx={{
          position: "absolute",
          right: 0,
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
        }}
      >
        {showRecalculateCacheIcon && (
          <IconButton
            size="large"
            color="inherit"
            variant="contained"
            onClick={reCalculateCachedData}
          >
            <Icon fontSize="medium">refresh</Icon>
          </IconButton>
        )}
        {reauthenticate && (
          <TooltipLight
            title={reauthenticateMessage || "Reauthenticate"}
            placement="bottom"
            variant="outline"
          >
            <IconButton
              size="large"
              color="inherit"
              variant="contained"
              sx={{
                color: "blue",
              }}
              onClick={() => window.open(oAuthStartUrl, "_blank")}
            >
              <Icon fontSize="medium">sync</Icon>
            </IconButton>
          </TooltipLight>
        )}
        {showRemoveAndCleanupIcon && (
          <IconButton
            size="large"
            color="inherit"
            variant="contained"
            sx={{
              color: "red",
            }}
            onClick={showDeletionAndCleanupModal}
          >
            <Icon fontSize="medium">delete</Icon>
          </IconButton>
        )}
        {documentationUrl && (
          <MuiLink href={documentationUrl} target="_blank" rel="noopener">
            <IconButton size="large" color="inherit" variant="contained">
              <Icon fontSize="medium">article</Icon>
            </IconButton>
          </MuiLink>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "4rem",
          justifyContent: "center",
        }}
      >
        {!integration?.detailedDescription ? (
          <Typography variant="h2">{integration.integrationName}</Typography>
        ) : null}
      </Box>

      {integration?.detailedDescription ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
            mb={1}
          >
            <IntegrationLogo width="250px" height="100px" logo={integration.integrationName} />
            <SyncAltIcon sx={{ mx: 4, color: "#000000", fontSize: "5rem !important" }} />
            <MDTypography variant="h1" color="black" fontSize="4.5rem">
              Alysio
            </MDTypography>
          </Box>
          <MDTypography variant="body1" mb={4}>
            {integration.detailedDescription}
          </MDTypography>
        </>
      ) : null}
      <ExpandedIntegrationForm
        integrationName={integrationName}
        integration={integration}
        saving={saving}
        hasChanges={hasChanges}
        isRemovingIntegration={isRemovingIntegration}
        onSubmit={onSubmit}
        setHasChanges={setHasChanges}
        handleAuthenticate={handleAuthenticate}
      />
      <MDSnackbar
        color="success"
        icon="checkCircleIcon"
        title="Success"
        content="Integration settings saved successfully"
        open={showSnackbar}
        onClose={closeSnackbar}
        close={closeSnackbar}
        bgWhite
      />
    </Box>
  );
}

export default ExpandedIntegration;
